import useSWR from 'swr';
import {
  getProductBannerComponent,
  getProductBannerPackages,
  ProductBannerEdgeCacheParams,
  ProductBannerPackagesEdgeCacheParams,
} from '@api/interfaces/componentApi';
import { getPersistedCartEmpty } from '@helpers/persistedCartEmpty';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useCustomer from '@hooks/useCustomer';
import { useSearchParams } from 'next/navigation';

type keyType = {
  id: string;
  filterQuery?: string;
  sortQuery?: string;
  edgeCacheParams?: ProductBannerEdgeCacheParams;
  packagesEdgeCacheParams?: ProductBannerPackagesEdgeCacheParams;
};

const fetcher = async ({ id, filterQuery, sortQuery, edgeCacheParams, packagesEdgeCacheParams }: keyType) => {
  const [productBanner, packageComponents] = await Promise.all([
    getProductBannerComponent(id, undefined, '999', undefined, filterQuery, sortQuery, edgeCacheParams),
    getProductBannerPackages(id, packagesEdgeCacheParams),
  ]);
  return { productBanner, packageComponents };
};

const useProductBannerItems = (id: string, displayFilters?: boolean, cartFilter?: string) => {
  const { store } = useStore();
  const { cart, isCartEmpty, hasFetchedCart } = useCart();
  const { customer } = useCustomer();
  const searchParams = useSearchParams();
  const filterQuery = (displayFilters && searchParams?.get('q')) || undefined;
  const sortQuery = (displayFilters && searchParams?.get('sort')) || undefined;

  // While the cart loads, fall back to the anonymous hint (see useSyncAnonymousCartEmptyHint).
  // SSR can't read localStorage, so the server says "not eligible" - harmless, since SWR only
  // fetches on the client. Deciding this server-side needs the hint in a cookie, and that cookie
  // in the CloudFront cache key.
  const isServer = typeof window === 'undefined';
  const cartEmptyForEdgeCache = hasFetchedCart ? isCartEmpty : !isServer && (getPersistedCartEmpty() ?? true);

  const isColdSpikeEligible =
    customer?.isAnonymous &&
    !store?.activelySelected &&
    !cart?.slot?.code &&
    cartEmptyForEdgeCache &&
    cartFilter === 'false' &&
    Boolean(store?.storeId);

  // CloudFront-only query params (stripped at origin; see ProductBannerEdgeCacheParams).
  const edgeCacheParams: ProductBannerEdgeCacheParams | undefined = isColdSpikeEligible
    ? {
        customerState: 'anonymous',
        storeState: store!.storeId!,
        slotState: 'empty',
        cartState: 'empty',
        cartFilter: 'false',
      }
    : undefined;

  // Packages: storeState for every session that has a store (ImageGrid can be
  // edge-cached for all users when origin emits public). customerState=anonymous
  // only for anonymous so non-public Small/Medium keeps today's anonymous gate.
  // Independent of the main-banner cold-spike slice (slot/cart/selected-store).
  const packagesEdgeCacheParams: ProductBannerPackagesEdgeCacheParams | undefined = store?.storeId
    ? customer?.isAnonymous
      ? { customerState: 'anonymous', storeState: store.storeId }
      : { storeState: store.storeId }
    : undefined;

  const key = {
    endpoint: '/productBannerComponent/{componentId} /productBannerComponent/packages/{componentId}',
    id,
    filterQuery,
    sortQuery,
    displayFilters,
    storeId: store?.storeId,
    slot: cart?.slot?.code,
    edgeCacheParams,
    packagesEdgeCacheParams,
  };

  // wait with fetching until store has been fetched (establishes base store in Hybris session)
  const shouldFetch = Boolean(store);

  const { data, error, isLoading, mutate } = useSWR(shouldFetch ? key : null, fetcher, { keepPreviousData: true });

  return {
    component: data?.productBanner,
    packages: data?.packageComponents as { [key: string]: ImageGridComponentType },
    isLoading,
    error,
    mutate,
  };
};

export default useProductBannerItems;
